/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useState } from 'react'

import type { ImportField, ImportRowInput, ImportRowResult } from './types'

const LABELS: Record<ImportField, string> = {
  email: 'Email',
  firstName: 'First name',
  lastName: 'Last name',
  company: 'Company',
  username: 'Username',
  dashboard: 'Copy dashboards from (email)',
}

export const COMPANY_LIST_ID = 'user-import-companies'

interface Props {
  row: ImportRowInput
  verdict?: ImportRowResult
  onSave: (row: ImportRowInput) => void
  onCancel: () => void
}

/**
 * Inline editor for one import row: the six CSV fields, plus the picks the server
 * asked for when an email matched several Fresh accounts or several profiles.
 * Saving re-checks the whole file.
 */
export const ImportRowEditor: React.FC<Props> = ({ row, verdict, onSave, onCancel }) => {
  const [draft, setDraft] = useState<ImportRowInput>(row)
  const flagged = new Set(verdict?.issues.filter((issue) => issue.level === 'error').map((issue) => issue.field))
  const set = (field: ImportField, value: string) => setDraft((current) => ({ ...current, [field]: value }))

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-base-200 p-3" data-testid="row-editor">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {(Object.keys(LABELS) as ImportField[]).map((field) => (
          <label key={field} className="flex flex-col gap-1 text-sm">
            <span className="text-xs uppercase tracking-wide text-base-content/50">{LABELS[field]}</span>
            <input
              className={`input input-bordered input-sm ${flagged.has(field) ? 'input-error' : ''}`}
              value={draft[field]}
              aria-label={LABELS[field]}
              list={field === 'company' ? COMPANY_LIST_ID : undefined}
              onChange={(e) => set(field, e.target.value)}
            />
          </label>
        ))}
      </div>

      {verdict?.freshCandidates && (
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase tracking-wide text-base-content/50">Which Cooltrax account is this?</span>
          <select
            className="select select-bordered select-sm"
            aria-label="Which Cooltrax account is this?"
            value={draft.freshUserId ?? ''}
            onChange={(e) =>
              setDraft((current) => ({
                ...current,
                freshUserId: e.target.value === '' ? undefined : Number(e.target.value),
              }))
            }
          >
            <option value="">Pick one…</option>
            {verdict.freshCandidates.map((candidate) => (
              <option key={candidate.userId} value={candidate.userId}>
                {candidate.username} — {candidate.firstName} {candidate.lastName}
                {candidate.companyName ? ` (${candidate.companyName})` : ''}
              </option>
            ))}
            <option value={0}>None of these — create a new account</option>
          </select>
        </label>
      )}

      {verdict?.dashboardCandidates && (
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase tracking-wide text-base-content/50">Copy dashboards from which user?</span>
          <select
            className="select select-bordered select-sm"
            aria-label="Copy dashboards from which user?"
            value={draft.dashboardSourceUid ?? ''}
            onChange={(e) => setDraft((current) => ({ ...current, dashboardSourceUid: e.target.value || undefined }))}
          >
            <option value="">Pick one…</option>
            {verdict.dashboardCandidates.map((candidate) => (
              <option key={candidate.uid} value={candidate.uid}>
                {candidate.displayName ?? candidate.uid} — {candidate.dashboardCount} dashboard
                {candidate.dashboardCount === 1 ? '' : 's'}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="flex gap-2">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => onSave(draft)}>
          Save and re-check
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}
